import React, { createContext, useContext, useEffect, useState } from 'react'
import supabase from '../supabaseClient'

type SunriseTheme = 'sunrise' | 'sunrise-dark'

interface ThemeContextType {
  theme: SunriseTheme
  setTheme: (theme: SunriseTheme) => Promise<void>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const normalizeTheme = (raw: string | null | undefined): SunriseTheme => {
  if (raw === 'sunrise-dark') return 'sunrise-dark'
  return 'sunrise'
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<SunriseTheme>('sunrise')

  useEffect(() => {
    const saved = normalizeTheme(localStorage.getItem('theme'))
    setThemeState(saved)
    document.documentElement.setAttribute('data-theme', saved)

    const loadUserTheme = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: preferences, error } = await supabase
        .from('user_preferences')
        .select('theme')
        .maybeSingle()

      if (error) {
        console.error('Error loading user preferences:', error)
        return
      }

      if (preferences?.theme) {
        const normalized = normalizeTheme(preferences.theme)
        setThemeState(normalized)
        document.documentElement.setAttribute('data-theme', normalized)
        localStorage.setItem('theme', normalized)
      }
    }

    loadUserTheme()
  }, [])

  const setTheme = async (newTheme: SunriseTheme) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('No user logged in')

      const { error } = await supabase
        .from('user_preferences')
        .upsert({ user_id: user.id, theme: newTheme }, { onConflict: 'user_id' })

      if (error) throw error
    } catch (error) {
      console.error('Error setting theme:', error)
    }

    setThemeState(newTheme)
    document.documentElement.setAttribute('data-theme', newTheme)
    localStorage.setItem('theme', newTheme)
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
